/**
 * Dashboard Frontend - File Service
 * Wraps @airclou/utils FileUploadService for dashboard-specific usage.
 */

import { FileUploadService, FileUploadResponse, FileUploadErrorCode } from '@airclou/utils';
import { getServiceUrl } from '@airclou/utils';

// Re-export types for consumers
export { FileUploadErrorCode };
export type { FileUploadResponse };

// Create configured file upload service instance
const uploadService = new FileUploadService({
  baseUrl: getServiceUrl('dashboard'),
  getAuthToken: () => localStorage.getItem('dashboard:authToken'),
  uploadEndpoint: '/api/files/upload',
});

export const fileService = {
  /**
   * Get the upload service instance (for use with UI components like ImageUploadModal)
   */
  getService: () => uploadService,

  /**
   * Upload a profile photo
   */
  async uploadProfilePhoto(file: File): Promise<FileUploadResponse> {
    return uploadService.uploadImage(file, {
      category: 'profiles',
      endpoint: '/api/files/profile-photo',
      maxSizeMB: 10,
    });
  },

  /**
   * Upload an organization logo
   */
  async uploadOrganizationLogo(file: File): Promise<FileUploadResponse> {
    return uploadService.uploadImage(file, {
      category: 'logos',
      endpoint: '/api/files/organization-logo',
      maxSizeMB: 5,
    });
  },

  /**
   * Upload a workspace logo
   */
  async uploadWorkspaceLogo(file: File, workspaceId: string): Promise<FileUploadResponse> {
    return uploadService.uploadImage(file, {
      category: 'workspaces',
      endpoint: '/api/files/workspace-logo',
      additionalFields: { workspace_id: workspaceId },
      maxSizeMB: 5,
    });
  },

  /**
   * Normalize a URL for display
   */
  normalizeUrl(url: string): string {
    return uploadService.normalizeUrl(url);
  },
};

export default fileService;
